import React, { useState, useMemo } from 'react';
import { Clock, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
/**
 * Timeline Component
 * Modern vertical timeline with date grouping, color-coded events, and expandable details
 */
const Timeline = ({ events = [] }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [copiedHash, setCopiedHash] = useState(null);
  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentcolor: "#0f172a",
    borderColor: "#DDE2EE"
  };
  // Parse relative time to actual date for grouping
  const parseRelativeTime = (relativeTime) => {
    const now = new Date();
    const timeStr = relativeTime.toLowerCase().trim();
    // Extract number from string (e.g., "2h ago" -> 2)
    const match = timeStr.match(/(\d+)/);
    if (!match) return now;
    const number = parseInt(match[1]);
    if (timeStr.includes('h ago')) {
      const date = new Date(now);
      date.setHours(date.getHours() - number);
      return date;
    } else if (timeStr.includes('d ago')) {
      const date = new Date(now);
      date.setDate(date.getDate() - number);
      return date;
    } else if (timeStr.includes('m ago')) {
      const date = new Date(now);
      date.setMinutes(date.getMinutes() - number);
      return date;
    }
    return now;
  };

  // Group events by date
  const groupedEvents = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: []
    };

    events.forEach((event, index) => {
      const eventDate = parseRelativeTime(event.timestamp);
      const eventWithDate = { ...event, actualDate: eventDate, originalIndex: index };

      if (eventDate >= today) {
        groups.today.push(eventWithDate);
      } else if (eventDate >= yesterday) {
        groups.yesterday.push(eventWithDate);
      } else if (eventDate >= weekAgo) {
        groups.thisWeek.push(eventWithDate);
      } else {
        groups.older.push(eventWithDate);
      }
    });

    return groups;
  }, [events]);

  const toggleExpand = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  // Copy to clipboard function
  const copyToClipboard = async (text, hashKey) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedHash(hashKey);
      setTimeout(() => setCopiedHash(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };


  // Combine all events in chronological order (newest first)
  const allEvents = useMemo(() => {
    return [
      ...groupedEvents.today,
      ...groupedEvents.yesterday,
      ...groupedEvents.thisWeek,
      ...groupedEvents.older
    ].sort((a, b) => b.actualDate - a.actualDate);
  }, [groupedEvents]);

  return (
    <div className="relative">
      <div className="relative space-y-4">
        {allEvents.map((event, index) => {
          const globalIndex = event.originalIndex;
          const isExpanded = expandedItems.has(globalIndex);
          
          return (
            <TimelineItem
              key={globalIndex}
              event={event}
              isExpanded={isExpanded}
              onToggleExpand={() => toggleExpand(globalIndex)}
              styles={styles}
              globalIndex={globalIndex}
              copyToClipboard={copyToClipboard}
              copiedHash={copiedHash}
            />
          );
        })}
      </div>
    </div>
  );
};

// TimelineItem Component
const TimelineItem = ({ event, isExpanded, onToggleExpand, styles, globalIndex, copyToClipboard, copiedHash }) => {
  const hasExpandableDetails = event.details?.logs || event.details?.nft_transfers || event.details?.erc20_transfers;

  return (
    <div className="relative">
      {/* Content card */}
      <div 
        className="p-4 transition-all duration-200"
        style={{
          backgroundColor: styles.primaryBg,
          borderLeft: `4px solid ${styles.accentColor}`,
          border: `1px solid ${styles.borderColor}`,
          borderLeftWidth: '1px',
          boxShadow: 'none'
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span 
              className="text-sm font-semibold"
              style={{ 
                color: styles.textPrimary, 
                fontFamily: 'Raleway, sans-serif' 
              }}
            >
              {event.type}
            </span>
          </div>
          <div 
            className="text-xs flex items-center gap-1"
            style={{ 
              color: styles.textSecondary, 
              fontFamily: 'Raleway, sans-serif' 
            }}
          >
            <Clock className="h-3 w-3" />
            {event.timestamp}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-2">
          {event.type === 'Session' && (
            <>
              <div className="text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                <span style={{ color: styles.textSecondary }}>Pages: </span>
                <strong>{event.details.pages}</strong>
                <span style={{ color: styles.textSecondary }}> | Duration: </span>
                <strong>{event.details.duration}</strong>
              </div>
              <div className="text-sm flex items-center gap-2" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                <span>Entry: <strong style={{ color: styles.textPrimary }}>{event.details.entry}</strong></span>
                <span>→</span>
                <span>Exit: <strong style={{ color: styles.textPrimary }}>{event.details.exit}</strong></span>
              </div>
            </>
          )}
          {event.type === 'Transaction' && (
            <>
              <div className="text-sm flex items-center gap-2" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                <span style={{ color: styles.textSecondary }}>Hash: </span>
                <strong className="font-mono">{event.details.hash}</strong>
                {event.details.hash && (
                  <button
                    onClick={() => copyToClipboard(event.details.hash, `hash-${globalIndex}`)}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title="Copy hash"
                  >
                    {copiedHash === `hash-${globalIndex}` ? (
                      <Check className="h-3 w-3" style={{ color: '#22c55e' }} />
                    ) : (
                      <Copy className="h-3 w-3" style={{ color: styles.textSecondary }} />
                    )}
                  </button>
                )}
              </div>
              <div className="text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                <span style={{ color: styles.textSecondary }}>Method: </span>
                <strong>{event.details.method}</strong>
                <span style={{ color: styles.textSecondary }}> | Value: </span>
                <strong>{event.details.value}</strong>
              </div>
              <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                Chain: {event.details.chain}
              </div>
            </>
          )}

          {event.type === 'Event' && (
            <>
              <div className="text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                <strong>{event.details.event}</strong>
              </div>
              <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                Page: {event.details.page}
              </div>
            </>
          )}
        </div>
        {/* Expandable Details */}
        {hasExpandableDetails && (
          <div className="mt-3">
            <button
              onClick={onToggleExpand}
              className="flex items-center gap-1 text-xs transition-colors"
              style={{ 
                color: styles.textSecondary, 
                fontFamily: 'Raleway, sans-serif' 
              }}
            >
              View More
              {isExpanded ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </button>

            {isExpanded && (
              <div className="mt-3 pt-3 space-y-3" style={{ borderTop: `1px solid ${styles.borderColor}` }}>
                {event.details.logs && (
                  <div>
                    <div className="text-xs font-semibold mb-1" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                      Logs
                    </div>
                    <div className="text-xs font-mono p-2 rounded" style={{ backgroundColor: styles.secondaryBg, color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                      {JSON.stringify(event.details.logs, null, 2)}
                    </div>
                  </div>
                )}
                {event.details.nft_transfers && (
                  <div>
                    <div className="text-xs font-semibold mb-1" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                      NFT Transfers
                    </div>
                    <div className="text-xs p-2 rounded" style={{ backgroundColor: styles.secondaryBg, color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                      {event.details.nft_transfers}
                    </div>
                  </div>
                )}
                {event.details.erc20_transfers && (
                  <div>
                    <div className="text-xs font-semibold mb-1" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                      ERC20 Transfers
                    </div>
                    <div className="text-xs p-2 rounded" style={{ backgroundColor: styles.secondaryBg, color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                      {event.details.erc20_transfers}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export default Timeline;

